'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { reportClientError } from '@/lib/reportClientError'
import { addBreadcrumb } from '@/lib/breadcrumbs'

interface RouteErrorProps {
  /** Error provided by Next.js to error.tsx files */
  error: Error & { digest?: string }
  /** Reset callback provided by Next.js — re-renders the segment */
  reset: () => void
  /** Optional secondary action — e.g. "Back to login" / "Contact your account manager" */
  secondaryHref?:  string
  secondaryLabel?: string
  /** Custom heading + body for context-specific copy */
  heading?: string
  body?:    string
}

/**
 * Shared route-level error boundary. Used by per-route-group `error.tsx`
 * files. Reports the error to the audit log via `reportClientError` and
 * pushes a breadcrumb so chained crashes are visible in the next report.
 *
 * Per-group wrappers customize copy and the secondary action so e.g. a
 * portal user gets "contact your account manager" instead of "try again".
 */
export default function RouteError({
  error,
  reset,
  secondaryHref,
  secondaryLabel,
  heading = 'Something went wrong',
  body    = 'An unexpected error occurred. Try refreshing, or go back and try again.',
}: RouteErrorProps) {
  useEffect(() => {
    addBreadcrumb({
      category: 'custom',
      message:  'route_error_boundary',
      data:     { msg: (error.message || '').slice(0, 120), digest: error.digest },
    })
    console.error('[obi-hub] route error:', error)
    reportClientError({
      error_type:      'react_boundary',
      message:         error.message || 'Route error boundary triggered',
      stack:           error.stack ?? null,
      component_stack: null,
    })
  }, [error])

  return (
    <div
      style={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        minHeight:      '60vh',
        gap: 'var(--space-4)',
        padding: 'var(--space-8)',
        fontFamily:     'var(--font-sans, system-ui, sans-serif)',
        color:          'var(--text-primary, #1a1a1a)',
      }}
    >
      <div
        style={{
          width:  48,
          height: 48,
          borderRadius:    12,
          background:      'var(--surface-subtle, #f5f5f5)',
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          fontSize:        22,
        }}
      >
        !
      </div>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{heading}</h2>
      <p
        style={{
          margin:    0,
          fontSize:  14,
          color:     'var(--text-secondary, #666)',
          textAlign: 'center',
          maxWidth:  400,
        }}
      >
        {body}
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
        <button
          onClick={reset}
          style={{
            padding:  '8px 20px',
            fontSize: 14,
            fontWeight: 500,
            border:     '1px solid var(--border, #e0e0e0)',
            borderRadius: 8,
            background: 'var(--bg-card, #fff)',
            color:      'var(--text-primary, #1a1a1a)',
            cursor:     'pointer',
          }}
        >
          Try again
        </button>
        {secondaryHref && secondaryLabel && (
          <Link
            href={secondaryHref}
            style={{
              padding:  '8px 20px',
              fontSize: 14,
              fontWeight: 500,
              border:     '1px solid var(--border, #e0e0e0)',
              borderRadius: 8,
              background: 'transparent',
              color:      'var(--text-secondary, #666)',
              textDecoration: 'none',
            }}
          >
            {secondaryLabel}
          </Link>
        )}
      </div>
    </div>
  )
}
